import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { Card } from '@/components/ui/Card';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { formatInteger } from '@/lib/format';
import { Can } from '@/lib/permissions';
import { NightEntranceInsightsDoc } from './entranceInsights';

interface InsightsRow {
  stageId?: string | null;
  label: string;
  sold: number;
  scanned: number;
  admitted: number;
  refused: number;
}

export function NightEntrancePanel({ nightId }: { nightId: string }) {
  return (
    <Can permission="entrance:read">
      <EntrancePanelInner nightId={nightId} />
    </Can>
  );
}

/**
 * Whole-number percentage label of `count` in `sold`. A non-zero count never
 * reads 0% (nor 100% when part of the total is missing), so a real no-show or
 * show-up always stays visible.
 */
function percentLabel(count: number, sold: number): string {
  if (count <= 0) return '0%';
  const pct = Math.round((count / sold) * 100);
  if (pct < 1) return '<1%';
  if (pct > 99 && count < sold) return '99%';
  return `${Math.min(pct, 100)}%`;
}

function ShowUpBar({ row }: { row: InsightsRow }) {
  if (row.sold <= 0) return <span className="text-gray-400">—</span>;
  const showUp = row.scanned;
  const noShow = Math.max(row.sold - row.scanned, 0);
  const showUpLabel = percentLabel(showUp, row.sold);
  const noShowLabel = percentLabel(noShow, row.sold);
  return (
    <div
      className="flex h-6 w-full min-w-[10rem] overflow-hidden rounded-md bg-gray-100 text-xs font-medium dark:bg-white/10"
      title={`Show-up ${showUpLabel} · No-show ${noShowLabel}`}
    >
      {showUp > 0 ? (
        <div
          className="flex items-center bg-gray-800 px-2 text-white dark:bg-gray-200 dark:text-gray-900"
          style={{ flex: `${showUp} 1 0`, minWidth: '2.5rem' }}
        >
          {showUpLabel}
        </div>
      ) : null}
      {noShow > 0 ? (
        <div
          className="flex items-center justify-end px-2 text-gray-600 dark:text-gray-300"
          style={{ flex: `${noShow} 1 0`, minWidth: '2.5rem' }}
        >
          {noShowLabel}
        </div>
      ) : null}
    </div>
  );
}

function EntrancePanelInner({ nightId }: { nightId: string }) {
  const { data, error } = useQuery(NightEntranceInsightsDoc, {
    variables: { nightId },
    errorPolicy: 'all',
  });
  const insights = data?.nightEntranceInsights;
  // The query stays mounted while the card is hidden, so the live
  // `entranceInsights` arm can make it appear on the first scan.
  if (!insights && !error) return null;
  if (insights && insights.total.scanned <= 0) return null;
  const lines: { row: InsightsRow; isTotal: boolean }[] = insights
    ? [
        ...insights.rows.map((row) => ({ row, isTotal: false })),
        { row: insights.total, isTotal: true },
      ]
    : [];

  return (
    <Card
      id="admin.nights.entranceInsights"
      title="Scans by sale stage"
      subtitle="Sold is purchased plus claimed tickets (holds do not count). Scanned is admitted plus refused, because both use up the ticket."
      actions={
        <Link
          to={`/entrance-scans?night=${nightId}`}
          className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Entrance scans report
        </Link>
      }
    >
      <div className="space-y-4">
        {error ? (
          <QueryErrorNotice error={error} title="Could not load entrance insights" />
        ) : null}
        {insights ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">Stage</th>
                  <th className="px-2 py-2 text-right">Sold</th>
                  <th className="px-2 py-2 text-right">Scanned</th>
                  <th className="px-2 py-2 text-right">Admitted</th>
                  <th className="px-2 py-2 text-right">Refused</th>
                  <th className="px-2 py-2 text-right">No-show</th>
                  <th className="px-2 py-2">Show-up rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {lines.map(({ row, isTotal }) => (
                  <tr
                    key={isTotal ? 'total' : (row.stageId ?? row.label)}
                    className={isTotal ? 'font-semibold' : undefined}
                  >
                    <td className="px-2 py-2">{row.label}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatInteger(row.sold)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatInteger(row.scanned)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatInteger(row.admitted)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatInteger(row.refused)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatInteger(Math.max(row.sold - row.scanned, 0))}
                    </td>
                    <td className="px-2 py-2">
                      <ShowUpBar row={row} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
